import React, { useState, useRef, useCallback } from 'react';
import '../css/ImageViewer.css';

import ThumbnailBar from "./ThumbnailBar";
import Bottomtabbar from './Bottomtabbar';

import { Info, X, Hand, Bookmark, Type, FileText, File, Layout } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';


type TabName = 'contents' | 'bookmark' | 'format' | 'text';
type ViewMode = 'image' | 'text';

interface ImageViewerProps {
  images: string[]; 
  productId: string;
  onExit: () => void;
}

// --- CONSTANTS ---
const MIN_SWIPE_DISTANCE = 80;
const MAX_TAP_DURATION = 200;
const MAX_SCALE = 2;
const MIN_SCALE = 0.5;

// Utility: get distance between two touches (for pinch zoom)
const getDistance = (touches: React.TouchList): number => {
  const dx = touches[0].clientX - touches[1].clientX;
  const dy = touches[0].clientY - touches[1].clientY;
  return Math.sqrt(dx * dx + dy * dy);
};

const ImageViewer: React.FC<ImageViewerProps> = ({ images, onExit, productId }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('image');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [scale, setScale] = useState(1);
  const [isUIVisible, setIsUIVisible] = useState(true);
  const [showInfo, setShowInfo] = useState(false);

  // --- TOUCH STATE ---
  const [touchStartX, setTouchStartX] = useState(0);
  const [touchEndX, setTouchEndX] = useState(0);
  const [touchStartTime, setTouchStartTime] = useState(0);

  // --- PINCH STATE ---
  const [isPinching, setIsPinching] = useState(false);
  const [initialPinchDistance, setInitialPinchDistance] = useState(0);
  const [baseScale, setBaseScale] = useState(1);

  const navigate = useNavigate();
  const location = useLocation();
  const imageWrapperRef = useRef<HTMLDivElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);

  // --- UTILITIES ---
  const toggleUIVisibility = () => setIsUIVisible((prev) => !prev);

  const jumpToImage = useCallback((index: number) => {
    setCurrentIndex(index);
    setScale(1);
  }, []);

  const goToNextImage = useCallback(() => {
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : prev));
    setScale(1);
  }, [images.length]);

  const goToPrevImage = useCallback(() => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev));
    setScale(1);
  }, []);

  const toggleViewMode = () => {
    setViewMode((prev) => (prev === 'image' ? 'text' : 'image'));
    setScale(1);
  };

  const handleTabClick = (tabName: TabName) => {
    console.log(`Bottom Tab Clicked: ${tabName}`);
  };

  // --- TOUCH HANDLERS ---
  const handleTouchStart = (e: React.TouchEvent) => {
    const touches = e.targetTouches;

    if (touches.length === 2) {
      setIsPinching(true);
      setBaseScale(scale);
      setInitialPinchDistance(getDistance(touches));
    } else if (touches.length === 1) {
      setIsPinching(false);
      setTouchStartX(touches[0].clientX);
      setTouchEndX(touches[0].clientX);
      setTouchStartTime(Date.now());
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const touches = e.targetTouches;

    if (isPinching && touches.length === 2) {
      const currentDistance = getDistance(touches);
      const zoomFactor = currentDistance / initialPinchDistance;
      let newScale = baseScale * zoomFactor;
      newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, newScale));
      setScale(newScale);
    } else if (!isPinching && touches.length === 1) {
      setTouchEndX(touches[0].clientX);
    }
  };

  const handleTouchEnd = () => {
    if (isPinching) {
      setIsPinching(false);
      setInitialPinchDistance(0);
      setBaseScale(scale);
      return;
    }

    const distance = touchStartX - touchEndX;
    const duration = Date.now() - touchStartTime;

    if (Math.abs(distance) < 20 && duration < MAX_TAP_DURATION) {
      toggleUIVisibility();
      return;
    }

    if (scale > 1.2) return;

    if (Math.abs(distance) > MIN_SWIPE_DISTANCE) {
      if (distance > 0) goToNextImage();
      else goToPrevImage();
    }
  };

  // --- RENDER ---
  return (
    <div className="book-viewer">
      {/* HEADER */}
      {isUIVisible && (
        <div className="header">
          <button
            onClick={() => {
              if (location.state?.from === 'home') navigate('/', { replace: true });
              else navigate(-1);
            }}
            className="header-btn back-btn"
          >
            <X size={18} />
          </button>

          <button className="header-title">Festive</button>

          <button
            onClick={() => setShowInfo(true)}
            className="header-btn info-btn"
            title="How to Navigate"
          >
            <Info size={20} />
          </button>
        </div>
      )}

      {/* IMAGE CONTAINER */}
      <div
        className="image-container"
        ref={imageContainerRef}
        onClick={toggleUIVisibility}
        onTouchStart={viewMode === 'image' ? handleTouchStart : undefined}
        onTouchMove={viewMode === 'image' ? handleTouchMove : undefined}
        onTouchEnd={viewMode === 'image' ? handleTouchEnd : undefined}
      >
        <div ref={imageWrapperRef} className="image-wrapper">
          {viewMode === 'image' ? (
            <img
              src={images[currentIndex]}
              alt={`Page ${currentIndex + 1}`}
              className="viewer-image"
              style={{ transform: `scale(${scale})` }}
            />
          ) : (
            <div className="text-view-content">
              <h2 className="text-xl font-bold mb-4">
                Text View: Page {currentIndex + 1}
              </h2>
              <p>[This text content should be loaded from a separate data source...]</p>
              <p className="mt-4 text-gray-500">
                The visual page rendering and zoom/pan are disabled in this mode.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* THUMBNAIL BAR */}
      {isUIVisible && (
        <ThumbnailBar
          images={images}
          currentIndex={currentIndex}
          onThumbnailClick={jumpToImage}
        />
      )}

      {/* BOTTOM TAB BAR */}
      {isUIVisible && (
        <div className="bottom-tab">
          <Bottomtabbar
            onTabClick={handleTabClick}
            viewMode={viewMode}
            toggleViewMode={toggleViewMode}
          />
        </div>
      )}

      {/* INFO POPUP */}
      {showInfo && (
        <div className="info-overlay" onClick={() => setShowInfo(false)}>
          <div
            className="info-box"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="info-header">
              <h2>How to Navigate</h2>
              <button className="close-info-btn" onClick={() => setShowInfo(false)}>
                <X size={18} />
              </button>
            </div>

            <div className="info-content">
              <div className="info-item">
                <Hand size={20} />
                <div>
                  <strong>Tap On Screen</strong>
                  <p>Displays navigation bar in reading mode</p>
                </div>
              </div>

             {/*<div className="info-item">
                <Bookmark size={20} />
                <div>
                  <strong>Bookmark</strong>
                  <p>Add article or page bookmarks for easy access later.</p>
                </div>
              </div>

              <div className="info-item">
                <Type size={20} />
                <div>
                  <strong>Text Tools</strong>
                  <p>Change font size, brightness, or theme to personalize your reading experience.</p>
                </div>
              </div>*/}

              <div className="info-item">
                <Hand size={20} />
                <div>
                  <strong>Swipe Left/Right</strong>
                  <p>Navigate through the article you will be able to view next pages </p>
                </div>
              </div>

              <div className="info-item">
                <Layout size={20} />
                <div>
                  <strong>Content Overview</strong>
                  <p>View previews of each page to find specific articles.</p>
                </div>
              </div>

              <div className="info-item">
                <FileText size={20} />
                <div>
                  <strong>Text View</strong>
                  <p>Displays a mobile-friendly text format for small-screen devices.</p>
                </div>
              </div>

              <div className="info-item">
                <File size={20} />
                <div>
                  <strong>PDF View</strong>
                  <p>Displays content in original PDF-page format.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageViewer;
